import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import BoxButton from 'components/BoxButton'
import Icon from 'components/Icon'
import { AsyncSelect, Select } from '../Select'
import useApi from 'hooks/useApi'

const YourVoice = ({ supplier: { status = null } = {}, match: { params: { supplierId } }, setCallback }) => {
  const { formatMessage } = useIntl()
  const [loaded, setLoaded] = useState(false)
  const [description, setDescription] = useState('')
  const [voiceGender, setVoiceGender] = useState(null)
  const [languages, setLanguages] = useState([])
  const [secondaryLanguages, setSecondaryLanguages] = useState([])
  const [allowUserToMakeMoreChangesToNativeLanguage, setAllowUserToMakeMoreChangesToNativeLanguage] = useState(false)
  const [year, setYear] = useState('')
  const [month, setMonth] = useState(undefined)
  const [day, setDay] = useState('')
  const [toneOfVoiceTags, setToneOfVoiceTags] = useState([])
  const [toneOfVoiceTagsOptions, loadingToneOfVoiceTagsOptions] = useApi(`/rest/v2/tag`)

  const [errors, setErrors] = useState({
    year: true,
    month: true,
    day: true,
    languages: true,
    voiceGender: true,
    toneOfVoiceTags: true
  })

  const [voiceAges, setVoiceAges] = useState({
    child: { name: 'child', icon: ['fal', 'child'], selected: false, href: "#" },
    young: { name: 'young', icon: ['fal', 'male'], selected: false, href: "#" },
    adult: { name: 'adult', icon: ['fal', 'running'], selected: false, href: "#" },
    senior: { name: 'senior', icon: ['fal', 'walking'], selected: false, href: "#" },
  })

  useEffect(() => {

    fetch(`/rest/v2/supplier/${supplierId}/tags`) 
      .then((response) => response.json())
      .then((tags) => setToneOfVoiceTags(tags.map(({id, name}) => ({label: name, value: id}))))

    const languages = fetch(`/rest/v2/supplier/${supplierId}/languages`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then((languages) => {
        setLanguages(languages
          .filter(({ native }) => Boolean(Number(native)))
          .map(({ uid, name }) => ({ value: uid, label: name }))
        )
        setSecondaryLanguages(languages
          .filter(({ native }) => !Boolean(Number(native)))
          .map(({ uid, name }) => ({ value: uid, label: name }))
        )
      })

    const data = fetch(`/rest/v2/supplier/${supplierId}`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then((supplier) => {
        setVoiceGender(supplier.gender ? parseInt(supplier.gender) : null)
        setDescription(supplier.commentsexternal || '')
        setYear(supplier.dob_year || '')
        setMonth(supplier.dob_month || undefined)
        setDay(supplier.dob_day || '')
      })

    Promise.all([languages, data])
      .then(() => setLoaded(true))
      .catch((error) => window.alert(error))
  }, [])

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      if (!loaded) {
        return reject('Loading')
      }

      const nextErrors = {
        day: !!day && day > 0 && day < 32,
        year: !!year,
        month: !!month,
        languages: !!languages && languages.length > 0,
        voiceGender: voiceGender !== null,
        toneOfVoiceTags: !!toneOfVoiceTags
      }

      if (Object.values(nextErrors).some((valid) => !valid)) {
        setErrors(nextErrors)

        event.stopPropagation()
        event.preventDefault()

        return reject(formatMessage(messages.validationFailed))
      }

      fetch(`/rest/v2/supplier/${supplierId}/tags`, {
        method: 'POST',
        body: JSON.stringify(toneOfVoiceTags.map(({ value, label }) => ({ id: value, name: label })))
      }).then((response) => response.json())

      fetch(`/rest/v2/supplier/${supplierId}`, {
        credentials: 'same-origin',
        method: 'PUT',
        body: JSON.stringify({
          dob_year: year,
          dob_month: month,
          dob_day: day,
          commentsexternal: description,
          gender: voiceGender,
          primary_language_ids: !languages ? [] : languages.map(({ value }) => value),
          secondary_language_ids: !secondaryLanguages ? [] : secondaryLanguages.map(({ value }) => value)
        }),
      })
        .then(resolve)
        .catch(reject)
    }))
  }, [loaded, year, day, month, description, voiceGender, errors, languages, secondaryLanguages, toneOfVoiceTags])

  const onVoiceAgeClick = (voiceAge) => {
    if (Object.values(voiceAges).filter((voiceAge) => (
      voiceAge.selected).length > 1 && !voiceAges[voiceAge].selected
    )) {
      return
    }

    setVoiceAges({
      ...voiceAges,
      [voiceAge]: {
        ...voiceAges[voiceAge],
        selected: !voiceAges[voiceAge].selected
      }
    })
  }

  return (
    <div>
      <div className="font-medium text-4xl">
        <FormattedMessage {...messages.title } />
      </div>

      <div className="text-gray-700 mt-6 w-2/3">
        <FormattedMessage {...messages.subtitle } />
      </div>


      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.languagesLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.languagesLabelHint } />
          </div>
        </legend>

        <AsyncSelect
          isOptionDisabled={() => !!languages && languages.length >= 1}
          isDisabled={status != 'ONBOARDING' && languages.length >= 1 && !allowUserToMakeMoreChangesToNativeLanguage}
          value={languages}
          onChange={(data) => {
            setAllowUserToMakeMoreChangesToNativeLanguage(true);
            setLanguages(data != null ? data : []);
          }}
          placeholder="Language"
          noOptionsMessage={({ inputValue }) => `No language found for '${inputValue}'`}
          promise={
            (query) => fetch(`/rest/v2/language?search[name]=${query}`, { credentials: 'same-origin' })
              .then((response) => response.json())
              .then((languages) => languages.map(({ uid, name }) => ({ value: uid, label: name })))
          }
        />

        { !errors.languages &&
          <div className="border border-gray-400 p-4 text-sm mt-6 text-gray-800 bg-red-200">
            <FormattedMessage {...messages.languagesWarning} />
          </div>
        }
      </fieldset>

      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.secondaryLanguagesLabel } />
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.secondaryLanguagesLabelHint } />
          </div>
        </legend>

        <AsyncSelect
          isOptionDisabled={({ value }) => (
            languages ? languages.map(({ value }) => value).includes(value) || (
              !!secondaryLanguages && secondaryLanguages.length > 1
            ) : false
          )}
          value={secondaryLanguages}
          onChange={setSecondaryLanguages}
          placeholder="Language"
          noOptionsMessage={({ inputValue }) => `No language found for '${inputValue}'`}
          promise={
            (query) => fetch(`/rest/v2/language?search[name]=${query}`, { credentials: 'same-origin' })
              .then((response) => response.json())
              .then((languages) => languages.map(({ uid, name }) => ({ value: uid, label: name })))
          }
        />
      </fieldset>

      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.voiceGenderLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.voiceGenderLabelHint } />
          </div>
        </legend>

        <div className="space-x-8">
          { [['other', 'transgender'], ['male', 'male'], ['female', 'female']].map(([gender, icon], i) => (
            <BoxButton
              icon={['fal', icon]}
              key={gender}
              selected={voiceGender == i}
              onClick={() => setVoiceGender(i)}
              tabIndex={0}
            >
              <FormattedMessage {...messages[`${gender}GenderLabel`]} />
            </BoxButton>
          )) }
        </div>

        { voiceGender == 0 &&
            <div className="border border-gray-400 p-4 text-sm mt-6 text-gray-800 bg-yellow-200">
              <FormattedMessage {...messages.otherGenderWarning} />
            </div>
        }
      </fieldset>

      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.birthdayLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.birthdayLabelHint } />
          </div>
        </legend>

        <div className="flex space-x-6">
          <span className={`w-3/6 p-4 focus:border-primary transition duration-200 border ${errors.month ? 'border-gray-400' : 'border-red-600'}`}>
            <select
              className="w-full"
              value={month}
              onChange={({ target: { value } }) => setMonth(value)}
            >
              <option value={undefined}>
                { formatMessage(messages.birthdayMonthPlaceholder) }
              </option>

              { formatMessage(messages.birthdayMonthOptions).split(',').map((name, i) => (
                <option value={i + 1} key={`option-${i}`}>{ name }</option>
              )) }
            </select>
          </span>

          <input
            value={day}
            onChange={({ target: { value } }) => setDay(value)}
            className={`w-2/6 p-4 focus:border-primary transition duration-200 border ${errors.day ? 'border-gray-400' : 'border-red-600'}`}
            type="number"
            name="day"
            placeholder={formatMessage(messages.birthdayDayPlaceholder)}
          />
          <input
            value={year}
            onChange={({ target: { value } }) => setYear(value)}
            className={`w-2/6 p-4 focus:border-primary transition duration-200 border ${errors.year ? 'border-gray-400' : 'border-red-600'}`}
            type="number"
            name="year"
            placeholder={formatMessage(messages.birthdayYearPlaceholder)}
          />
        </div>
      </fieldset>

      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.toneOfVoiceTagsLabel } /> *
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.toneOfVoiceTagsHint } />
          </div>
        </legend>
        <Select
          styles={{control: (provided, state) => ({
            ...provided,
            borderColor: !errors.toneOfVoiceTags ? '#DC2626' : (state.isFocused ? '#2684FF' : '#9CA3AF')
          })}}
          isMulti
          isDisabled={loadingToneOfVoiceTagsOptions}
          value={toneOfVoiceTags}
          onChange={(data) => (!data || data.length <= 3) ? setToneOfVoiceTags(data) : undefined}
          options={toneOfVoiceTagsOptions ? toneOfVoiceTagsOptions.map(({uid, name}) => ({label: name, value: uid})) : null}
        />
      </fieldset>
      <fieldset className="block mt-12">
        <legend className="py-6">
          <FormattedMessage {...messages.descriptionLabel } />
          <div className="text-sm text-gray-700" >
            <FormattedMessage {...messages.descriptionLabelHint } />
          </div>
        </legend>
        <textarea
          value={description}
          onChange={({ target: { value } }) => {
            if (value.length < 501) {
              setDescription(value)
            }
          }}
          placeholder={formatMessage(messages.descriptionPlaceholder)}
          className={`w-full resize-y border p-4 focus:border-primary transition duration-200 border-gray-400`}
        />
      </fieldset>
      <p className="text-xs py-2 font-light text-gray-600">
        <FormattedMessage {...messages.descriptionHint} values={{ left: 500 - description.length, max: 500 }} />
      </p>
    </div>
  )
}

const VoiceAges = () => (
  <fieldset className="block mt-12">
    <legend className="py-6">
      <FormattedMessage {...messages.voiceAgesLabel } /> *
      <div className="text-sm text-gray-700" >
        <FormattedMessage {...messages.voiceAgesLabelHint } />
      </div>
    </legend>

    <div className="space-x-8">
      { Object.values(voiceAges).map((voiceAge) => (
        <div className="inline-block">
          <BoxButton icon={voiceAge.icon} key={voiceAge.name} selected={voiceAge.selected} onClick={() => onVoiceAgeClick(voiceAge.name)}>
            <FormattedMessage {...messages[`${voiceAge.name}Label`]} />
          </BoxButton>
          <a
            target="_blank"
            title={formatMessage(messages.voiceAgesExampleTitle)}
            className="block text-xs text-gray-600 hover:text-primary mt-1 cursor-pointer"
            href={voiceAge.href}
          >
            <FormattedMessage {...messages.voiceAgesExampleLabel } />
          </a>
        </div>
      )) }
    </div>
  </fieldset>
)


export default YourVoice
